/* global globalThis */
const MASK_CHAR = "*";
export const getMaskedMemberId = (id, showPHI = false) => {
  let enabled;
  if (typeof showPHI === "boolean") {
    enabled = showPHI;
  } else if (globalThis.window === undefined) {
    enabled = false;
  } else {
    enabled = !!globalThis.window.showPHI;
  }
  if (enabled) return id;
  if (!id || id.length <= 2) return id;
  return MASK_CHAR.repeat(id.length - 2) + id.slice(-2);
};

const maskPart = (part) => {
  if (!part) return part;
  if (part.length === 1) return part; // single-letter initials stay as-is
  return part[0] + MASK_CHAR.repeat(part.length - 1);
};

/**
 * Return masked name according to options.
 * - maskMiddle: 'initial-only' | 'mask' | 'keep'
 *    'initial-only' -> middle shown as single initial (if present)
 *    'mask' -> middle shown as first letter + stars
 *    'keep' -> middle shown unmasked
 */
export const maskPersonName = (fullName, showPHI = false, options = {}) => {
  const { maskMiddle = "initial-only" } = options || {};
  let enabled;
  if (typeof showPHI === "boolean") {
    enabled = showPHI;
  } else if (globalThis.window === undefined) {
    enabled = false;
  } else {
    enabled = !!globalThis.window.showPHI;
  }
  if (!fullName) return fullName || "";
  const parts = fullName.split(/\s+/).filter(Boolean);
  if (enabled) return parts.join(" ");

  const first = parts[0] ? maskPart(parts[0]) : "";
  let middle = "";
  let last = "";
  if (parts.length === 1) {
    return first;
  } else if (parts.length === 2) {
    last = maskPart(parts[1]);
  } else {
    middle = parts.slice(1, -1).join(" ");
    last = maskPart(parts[parts.length - 1]);
  }

  // Handle middle according to option
  let middleOut = "";
  if (middle) {
    const middleParts = middle.split(/\s+/).filter(Boolean);
    middleOut = middleParts
      .map((m) => {
        if (maskMiddle === "keep") return m;
        if (maskMiddle === "mask") return maskPart(m);
        // initial-only
        return m[0] || "";
      })
      .join(" ");
  }

  const out = [first, middleOut, last].filter(Boolean).join(" ");
  return out;
};

export const getNameVariants = (fullName, showPHI = false) => {
  return {
    initialsOnly: maskPersonName(fullName, showPHI, {
      maskMiddle: "initial-only",
    }),
    maskedMiddle: maskPersonName(fullName, showPHI, { maskMiddle: "mask" }),
    full: maskPersonName(fullName, showPHI, { maskMiddle: "keep" }),
  };
};

/**
 * Mask all characters except the last `n` characters.
 * For IDs we keep the original characters but replace leading chars with the mask symbol.
 * If `showPHI` is truthy the original value is returned.
 *
 * @param {string|number} value
 * @param {number} n - number of trailing characters to keep (default 2)
 * @param {boolean} showPHI
 * @returns {string}
 */
export const maskAllButLast = (value, n = 2, showPHI = false) => {
  let enabled;
  if (typeof showPHI === "boolean") {
    enabled = showPHI;
  } else if (globalThis.window === undefined) {
    enabled = false;
  } else {
    enabled = !!globalThis.window.showPHI;
  }
  if (enabled) return value == null ? "" : String(value);
  if (value == null) return "";
  const s = String(value);
  if (s.length <= n) return s;
  return MASK_CHAR.repeat(Math.max(0, s.length - n)) + s.slice(-n);
};

/**
 * Mask phone number digits keeping only the last 2 digits visible.
 * Non-digit characters are ignored when counting; the returned string is the masked digits.
 * If `showPHI` is truthy the original input string is returned.
 *
 * @param {string|number} phone
 * @param {boolean} showPHI
 * @returns {string}
 */
export const maskPhoneLast4 = (phone, showPHI = false) => {
  let enabled;
  if (typeof showPHI === "boolean") {
    enabled = showPHI;
  } else if (globalThis.window === undefined) {
    enabled = false;
  } else {
    enabled = !!globalThis.window.showPHI;
  }
  if (enabled) return phone == null ? "" : String(phone);
  if (phone == null) return "";
  const digits = String(phone).replaceAll(/\D/g, "");
  if (digits.length <= 2) return digits;
  return MASK_CHAR.repeat(Math.max(0, digits.length - 2)) + digits.slice(-2);
};
export const maskEmail = (email, showPHI = false) => {
  if (!email) return "";

  let enabled;
  if (typeof showPHI === "boolean") {
    enabled = showPHI;
  } else if (globalThis.window === undefined) {
    enabled = false;
  } else {
    enabled = !!globalThis.window.showPHI;
  }

  if (enabled) return email;

  const [user, domain] = email.split("@");
  if (!domain) return email;

  return MASK_CHAR.repeat(user.length) + "@" + domain;
};

/**
 * Mask text showing only the first character (initial).
 * If `showPHI` is truthy the original value is returned.
 *
 * @param {string|number} value
 * @param {boolean} showPHI
 * @returns {string}
 */
export const maskShowInitialOnly = (value, showPHI = false) => {
  let enabled;
  if (typeof showPHI === "boolean") {
    enabled = showPHI;
  } else if (globalThis.window === undefined) {
    enabled = false;
  } else {
    enabled = !!globalThis.window.showPHI;
  }

  if (enabled) return value == null ? "" : String(value);
  if (value == null) return "";

  const s = String(value);
  if (s.length === 0) return "";
  if (s.length === 1) return s;

  return s[0] + MASK_CHAR.repeat(s.length - 1);
};

/**
 * Masks all characters in a string with asterisks
 * @param {string|number} value - The value to mask
 * @param {boolean} showPHI - If true, returns the original value
 * @returns {string} Fully masked string or original value
 */
export const maskAll = (value, showPHI = false) => {
  let enabled;
  if (typeof showPHI === "boolean") {
    enabled = showPHI;
  } else if (globalThis.window === undefined) {
    enabled = false;
  } else {
    enabled = !!globalThis.window.showPHI;
  }

  if (enabled) return value == null ? "" : String(value);
  if (value == null) return "";

  const str = String(value);
  return "*".repeat(str.length);
};

/**
 * Mask city name showing only the first letter followed by stars based on length
 * Example: "CARY" -> "C***", "NY" -> "N*"
 */
export const maskCity = (city, showPHI = false) => {
  return maskShowInitialOnly(city, showPHI);
};

/**
 * Mask address line showing *** for house number + first letter of street + stars based on length
 * Example: "108 FELSPAR WAY" -> "*** F****** ***"
 */
export const maskAddressLine = (addressLine, showPHI = false) => {
  const enabled =
    typeof showPHI === "boolean" ? showPHI : !!globalThis.window?.showPHI;

  if (enabled) return addressLine || "";
  if (!addressLine) return "";

  const parts = String(addressLine).trim().split(/\s+/);
  if (parts.length === 0) return "";

  if (parts.length === 1) {
    return maskShowInitialOnly(parts[0], showPHI);
  }

  // Mask first part (house number) as ***, show first letter + ****** for street name, *** for remaining
  const maskedParts = [
    MASK_CHAR.repeat(3),
    parts[1] ? parts[1][0] + MASK_CHAR.repeat(6) : null,
    ...parts.slice(2).map(() => MASK_CHAR.repeat(3)),
  ].filter(Boolean);

  return maskedParts.join(" ");
};

/**
 * Mask state showing only the first letter followed by stars based on length
 * Example: "NC" -> "N*", "CA" -> "C*"
 */
export const maskState = (state, showPHI = false) => {
  return maskShowInitialOnly(state, showPHI);
};

/**
 * Mask postal code showing only the last 2 digits
 * Example: "27518" -> "***18", "12345" -> "***45"
 */
export const maskPostalCode = (postalCode, showPHI = false) => {
  return maskAllButLast(postalCode, 2, showPHI);
};

export const maskDOB = (dob, showPHI = false) => {
  const enabled =
    typeof showPHI === "boolean" ? showPHI : !!globalThis.window?.showPHI;

  if (enabled) return dob || "";
  if (!dob) return "";

  const raw = String(dob).trim();
  let normalized = raw;

  // Normalize to a stable UTC date string when possible.
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    normalized = parsed.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  }

  // Use the last numeric digit so suffix is deterministic for dates.
  const digitsOnly = normalized.replaceAll(/\D/g, "");
  const lastDigit = digitsOnly.slice(-1);

  return lastDigit ? `**/**/***${lastDigit}` : "**/**/****";
};
